import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { ChevronLeft } from "lucide-react";

import { api } from "../../api";
import { Button } from "../../components/ui/button";
import { useLanguage } from "../../contexts/LanguageContext";
import { navigate } from "../../lib/router";
import { settingsLabels } from "./labels";
import { ExceptionsTab } from "./components/ExceptionsTab";
import { SessionsTab } from "./components/SessionsTab";
import { NewExceptionModal } from "./components/NewExceptionModal";
import { cn } from "../../lib/utils";

export default function ScheduleExceptionsPage() {
  const { lang } = useLanguage();
  const t = settingsLabels[lang].exceptions;

  const [exceptions, setExceptions] = useState<any[]>([]);
  const [sessions, setSessions] = useState<any[]>([]);
  const [professionals, setProfessionals] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<"exceptions" | "sessions">("sessions");
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Form state
  const [employeeId, setEmployeeId] = useState<string>("all");
  const [date, setDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [allDay, setAllDay] = useState(true);
  const [startTime, setStartTime] = useState("08:00");
  const [endTime, setEndTime] = useState("18:00");
  const [description, setDescription] = useState("");
  const [repeatType, setRepeatType] = useState<"none" | "weekly" | "monthly">("none");

  const weekdayNames = useMemo(() => {
    if (lang === "pt") return ["Domingo", "Segunda-feira", "Terça-feira", "Quarta-feira", "Quinta-feira", "Sexta-feira", "Sábado"];
    return ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  }, [lang]);

  const selectedWeekday = useMemo(() => {
    if (!date) return null;
    const d = new Date(date + "T00:00:00");
    return d.getDay();
  }, [date]);

  const selectedDayOfMonth = useMemo(() => {
    if (!date) return null;
    const d = new Date(date + "T00:00:00");
    return d.getDate();
  }, [date]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [excRes, profRes, sessRes] = await Promise.all([
        api.listScheduleExceptions(),
        api.listProfessionals(),
        api.listCompanySessions(),
      ]);
      if (excRes.ok) setExceptions(excRes.data);
      if (profRes.ok) setProfessionals(profRes.data);
      if (sessRes.ok) setSessions(sessRes.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;

    if (!date) {
      toast.error("Data é obrigatória");
      return;
    }

    let start_datetime: string;
    let end_datetime: string;

    if (allDay) {
      start_datetime = `${date} 00:00:00`;
      end_datetime = `${date} 23:59:59`;
    } else {
      if (!startTime || !endTime) {
        toast.error("Horários são obrigatórios");
        return;
      }
      start_datetime = `${date} ${startTime}:00`;
      end_datetime = `${date} ${endTime}:00`;
    }

    setSaving(true);
    try {
      const res = await api.createScheduleException({
        employee_id: employeeId === "all" ? null : Number(employeeId),
        start_datetime,
        end_datetime,
        description,
        repeat_weekday: repeatType === "weekly" ? selectedWeekday : null,
        repeat_month_day: repeatType === "monthly" ? selectedDayOfMonth : null,
      });

      if (res.ok) {
        toast.success(t.toasts.success);
        setDescription("");
        setIsModalOpen(false);
        loadData();
      } else {
        toast.error(res.error || t.toasts.error);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Tem certeza?")) return;
    try {
      const res = await api.deleteScheduleException(id);
      if (res.ok) {
        toast.success(t.toasts.deleted);
        loadData();
      } else {
        toast.error(t.toasts.error);
      }
    } catch {
      toast.error(t.toasts.error);
    }
  };

  const handleAddSession = async (weekday: number) => {
    const start_time = "08:00";
    const duration_minutes = 60;
    try {
      const res = await api.createCompanySession({ weekday, start_time, duration_minutes });
      if (res.ok) {
        loadData();
        toast.success(lang === 'pt' ? "Horário adicionado" : "Hours added");
      }
    } catch (e) {
      toast.error("Erro ao adicionar");
    }
  };

  const handleDeleteSession = async (id: number) => {
    try {
      const res = await api.deleteCompanySession(id);
      if (res.ok) {
        loadData();
      }
    } catch (e) {
      toast.error("Erro ao excluir");
    }
  };

  const handleUpdateSession = async (id: number, data: { start_time?: string; end_time?: string }) => {
    const sess = sessions.find(s => s.id === id);
    if (!sess) return;

    let start_time = data.start_time || sess.start_time;
    let duration_minutes = sess.duration_minutes;

    if (data.end_time) {
        const [h1, m1] = start_time.split(":").map(Number);
        const [h2, m2] = data.end_time.split(":").map(Number);
        duration_minutes = (h2 * 60 + m2) - (h1 * 60 + m1);
        if (duration_minutes <= 0) {
            toast.error(lang === 'pt' ? "Horário de término deve ser após o início" : "End time must be after start time");
            return;
        }
    } else if (data.start_time) {
        const [h1, m1] = sess.start_time.split(":").map(Number);
        const currentEndMinutes = (h1 * 60 + m1) + sess.duration_minutes;
        const [h2, m2] = data.start_time.split(":").map(Number);
        const newStartMinutes = h2 * 60 + m2;
        duration_minutes = currentEndMinutes - newStartMinutes;
        if (duration_minutes <= 0) {
            duration_minutes = 30; 
        }
    }

    try {
        const res = await api.updateCompanySession(id, { start_time, duration_minutes });
        if (res.ok) {
            loadData();
        }
    } catch (e) {
        toast.error("Erro ao atualizar");
    }
  };

  return (
    <div className="mx-auto w-full max-w-5xl p-4 md:p-6 space-y-8 animate-in fade-in duration-500">
      {/* Premium Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={() => navigate("/app/settings")}
          className="rounded-xl hover:bg-neutral-100 transition-all active:scale-95"
        >
          <ChevronLeft className="h-6 w-6 text-neutral-600" />
        </Button>
        <div className="space-y-1">
          <h1 className="text-2xl font-bold text-neutral-800 tracking-tight">{t.header}</h1>
          <p className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest leading-none">
            {t.sessions.description}
          </p>
        </div>
      </div>

      {/* Segmented Control (Tabs) */}
      <div className="flex p-1 bg-neutral-100 rounded-2xl w-fit shadow-inner">
        <button
          onClick={() => setActiveTab("sessions")}
          className={cn(
            "px-6 py-2 rounded-xl text-[10px] font-bold transition-all uppercase tracking-widest",
            activeTab === "sessions" 
              ? "bg-white text-neutral-900" 
              : "text-neutral-400 hover:text-neutral-600"
          )}
        >
          {t.tabs.sessions}
        </button>
        <button
          onClick={() => setActiveTab("exceptions")}
          className={cn(
            "px-6 py-2 rounded-xl text-[10px] font-bold transition-all uppercase tracking-widest",
            activeTab === "exceptions" 
              ? "bg-white text-neutral-900" 
              : "text-neutral-400 hover:text-neutral-600"
          )}
        >
          {t.tabs.exceptions}
        </button>
      </div>

      {activeTab === "exceptions" ? (
        <ExceptionsTab
          loading={loading}
          exceptions={exceptions}
          professionals={professionals}
          weekdayNames={weekdayNames}
          lang={lang}
          handleDelete={handleDelete}
          onAddClick={() => setIsModalOpen(true)}
        />
      ) : (
        <SessionsTab
          sessions={sessions}
          weekdayNames={weekdayNames}
          handleAddSession={handleAddSession}
          handleUpdateSession={handleUpdateSession}
          handleDeleteSession={handleDeleteSession}
        />
      )}

      <NewExceptionModal
        isOpen={isModalOpen}
        onOpenChange={setIsModalOpen}
        handleSubmit={handleSubmit}
        employeeId={employeeId}
        setEmployeeId={setEmployeeId}
        professionals={professionals}
        date={date}
        setDate={setDate}
        repeatType={repeatType}
        setRepeatType={setRepeatType}
        selectedWeekday={selectedWeekday}
        selectedDayOfMonth={selectedDayOfMonth}
        weekdayNames={weekdayNames}
        allDay={allDay}
        setAllDay={setAllDay}
        startTime={startTime}
        setStartTime={setStartTime}
        endTime={endTime}
        setEndTime={setEndTime}
        description={description}
        setDescription={setDescription}
        saving={saving}
      />
    </div>
  );
}
